import { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const data = await adminApi.getPlans();
      setPlans(data.plans || []);
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = async (planData) => {
    try {
      if (editingPlan) {
        await adminApi.updatePlan(editingPlan.id, planData);
      } else {
        await adminApi.createPlan(planData);
      }
      setEditingPlan(null);
      setShowCreateModal(false);
      loadPlans();
    } catch (error) {
      console.error('Failed to save plan:', error);
      alert('Failed to save plan: ' + error.message);
    }
  };

  const handleDeletePlan = async (id) => {
    if (!confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      await adminApi.deletePlan(id);
      loadPlans();
    } catch (error) {
      console.error('Failed to delete plan:', error);
      alert('Failed to delete plan: ' + error.message);
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await adminApi.updatePlan(plan.id, { is_active: !plan.is_active });
      loadPlans();
    } catch (error) {
      console.error('Failed to toggle plan:', error);
      alert('Failed to update plan: ' + error.message);
    }
  };

  const handleToggleFeatured = async (plan) => {
    try {
      // Unfeature all other plans first
      for (const p of plans) {
        if (p.is_featured && p.id !== plan.id) {
          await adminApi.updatePlan(p.id, { is_featured: false });
        }
      }
      await adminApi.updatePlan(plan.id, { is_featured: !plan.is_featured });
      loadPlans();
    } catch (error) {
      console.error('Failed to toggle featured:', error);
      alert('Failed to update plan: ' + error.message);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-gray-900">Pricing Plans</h1>
          <p className="text-gray-500 mt-1">Manage your subscription tiers and pricing</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2"
        >
          <span>+</span>
          <span>Add Plan</span>
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {plans.map(plan => (
          <div 
            key={plan.id} 
            className={`bg-white rounded-xl shadow-sm p-6 relative ${
              !plan.is_active ? 'opacity-60' : ''
            } ${plan.is_featured ? 'ring-2 ring-primary-500' : ''}`}
          >
            {plan.is_featured && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Most Popular
                </span>
              </div>
            )}

            {!plan.is_active && (
              <div className="absolute top-2 right-2">
                <span className="bg-gray-200 text-gray-600 text-xs font-semibold px-2 py-1 rounded">
                  Inactive
                </span>
              </div>
            )}

            <div className="text-center mb-4">
              <h3 className="font-display text-xl font-bold text-gray-900">{plan.name}</h3>
              <div className="mt-2">
                <span className="text-4xl font-bold text-gray-900">${parseFloat(plan.price).toFixed(0)}</span>
                <span className="text-gray-500">/{plan.billing_period}</span>
              </div>
            </div>

            <ul className="space-y-2 mb-6">
              {(typeof plan.features === 'string' ? JSON.parse(plan.features) : plan.features || []).map((feature, idx) => (
                <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between text-xs text-gray-500">
                <span>Max Levels:</span>
                <span>{(typeof plan.limits === 'string' ? JSON.parse(plan.limits) : plan.limits)?.maxLevels === -1 ? 'Unlimited' : (typeof plan.limits === 'string' ? JSON.parse(plan.limits) : plan.limits)?.maxLevels}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Max Children:</span>
                <span>{(typeof plan.limits === 'string' ? JSON.parse(plan.limits) : plan.limits)?.maxChildren === -1 ? 'Unlimited' : (typeof plan.limits === 'string' ? JSON.parse(plan.limits) : plan.limits)?.maxChildren}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Button Text:</span>
                <span>{plan.button_text}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
              <button
                onClick={() => setEditingPlan(plan)}
                className="flex-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Edit
              </button>
              <button
                onClick={() => handleToggleActive(plan)}
                className={`flex-1 px-3 py-1.5 text-sm rounded ${
                  plan.is_active 
                    ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' 
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {plan.is_active ? 'Deactivate' : 'Activate'}
              </button>
              <button
                onClick={() => handleToggleFeatured(plan)}
                className={`flex-1 px-3 py-1.5 text-sm rounded ${
                  plan.is_featured 
                    ? 'bg-primary-100 text-primary-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {plan.is_featured ? '★ Featured' : 'Feature'}
              </button>
              {plan.id !== 'free' && (
                <button
                  onClick={() => handleDeletePlan(plan.id)}
                  className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>



      {/* Edit/Create Modal */}
      {(editingPlan || showCreateModal) && (
        <PlanModal
          plan={editingPlan}
          onClose={() => { setEditingPlan(null); setShowCreateModal(false); }}
          onSave={handleSavePlan}
        />
      )}
    </div>
  );
}

// ============================================
// PLAN EDIT/CREATE MODAL
// ============================================
function PlanModal({ plan, onClose, onSave }) {
  const isEditing = !!plan;
  
  const [formData, setFormData] = useState({
    id: plan?.id || '',
    name: plan?.name || '',
    price: plan?.price || 0,
    billing_period: plan?.billing_period || 'month',
    features: typeof plan?.features === 'string' ? JSON.parse(plan.features) : (plan?.features || []),
    limits: typeof plan?.limits === 'string' ? JSON.parse(plan.limits) : (plan?.limits || { maxLevels: 20, maxChildren: 1, progressTracking: false, therapistAccess: false }),
    is_active: plan?.is_active !== false,
    is_featured: plan?.is_featured || false,
    sort_order: plan?.sort_order || 0,
    button_text: plan?.button_text || 'Get Started'
  });

  const [newFeature, setNewFeature] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSave(formData);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            {isEditing ? `Edit Plan: ${plan.name}` : 'Create New Plan'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan ID</label>
              <input
                type="text"
                required
                disabled={isEditing}
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                placeholder="e.g., basic, premium, enterprise"
              />
              {!isEditing && <p className="text-xs text-gray-500 mt-1">Lowercase, no spaces (used internally)</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Basic, Premium, Enterprise"
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing Period</label>
              <select
                value={formData.billing_period}
                onChange={(e) => setFormData({ ...formData, billing_period: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="forever">Forever (Free)</option>
                <option value="month">Per Month</option>
                <option value="year">Per Year</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input
                type="number"
                min="0"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Button Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
            <input
              type="text"
              value={formData.button_text}
              onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., Get Started, Start Free Trial, Upgrade Now"
            />
          </div>

          {/* Features */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Features (shown as bullet points)</label>
            <div className="space-y-2 mb-3">
              {formData.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <span className="text-green-500">✓</span>
                  <span className="flex-1 text-sm">{feature}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFeature(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Add a feature..."
              />
              <button
                type="button"
                onClick={handleAddFeature}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Add
              </button>
            </div>
          </div>

          {/* Limits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Plan Limits</label>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max Levels (-1 = unlimited)</label>
                <input
                  type="number"
                  value={formData.limits.maxLevels}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    limits: { ...formData.limits, maxLevels: parseInt(e.target.value) } 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max Children (-1 = unlimited)</label>
                <input
                  type="number"
                  value={formData.limits.maxChildren}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    limits: { ...formData.limits, maxChildren: parseInt(e.target.value) } 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="progressTracking"
                  checked={formData.limits.progressTracking}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    limits: { ...formData.limits, progressTracking: e.target.checked } 
                  })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="progressTracking" className="text-sm text-gray-700">Progress Tracking</label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="therapistAccess"
                  checked={formData.limits.therapistAccess}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    limits: { ...formData.limits, therapistAccess: e.target.checked } 
                  })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="therapistAccess" className="text-sm text-gray-700">Therapist Access</label>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex space-x-6">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-gray-300"
              />
              <label htmlFor="is_active" className="text-sm text-gray-700">Active (visible on pricing page)</label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_featured"
                checked={formData.is_featured}
                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                className="rounded border-gray-300"
              />
              <label htmlFor="is_featured" className="text-sm text-gray-700">Featured (highlighted as "Most Popular")</label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Plan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
